import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useGlobalSettings } from '@/hooks/useSettings'
import { cn } from '@/lib/utils'
import type { TelegramDestination } from '@/lib/api'
import {
  EMPTY_TELEGRAM_DESTINATION,
  findTelegramDestination,
  telegramTargetKey,
} from '@/lib/telegramDestinations'

type TelegramDestinationFilter = 'all' | 'channels' | 'topics'

interface TelegramDestinationSelectProps {
  id?: string
  chatId?: string | number | null
  replyToMessageId?: string | number | null
  destinations?: TelegramDestination[]
  onChange: (target: {
    chat_id: string
    reply_to_message_id?: string | null
    destination?: TelegramDestination | null
  }) => void
  allowEmpty?: boolean
  emptyLabel?: string
  placeholder?: string
  filter?: TelegramDestinationFilter
  disabled?: boolean
  className?: string
  triggerClassName?: string
}

function normalizeValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function customDestinationLabel(chatId?: string | number | null, replyToMessageId?: string | number | null): string {
  const chat = normalizeValue(chatId) || 'default chat'
  const topic = normalizeValue(replyToMessageId)
  return topic ? `Custom/legacy (${chat} / topic ${topic})` : `Custom/legacy (${chat})`
}

function filterDestinations(
  destinations: TelegramDestination[],
  filter: TelegramDestinationFilter
): TelegramDestination[] {
  if (filter === 'channels') {
    return destinations.filter((destination) => !normalizeValue(destination.reply_to_message_id))
  }
  if (filter === 'topics') {
    return destinations.filter((destination) => normalizeValue(destination.reply_to_message_id))
  }
  return destinations
}

export function TelegramDestinationSelect({
  id,
  chatId,
  replyToMessageId,
  destinations,
  onChange,
  allowEmpty = true,
  emptyLabel = 'Use global default',
  placeholder = 'Select destination',
  filter = 'all',
  disabled = false,
  className,
  triggerClassName,
}: TelegramDestinationSelectProps) {
  const { data: settings } = useGlobalSettings()
  const options = filterDestinations(destinations ?? settings?.telegram_destinations ?? [], filter)
  const selected = findTelegramDestination(options, chatId, replyToMessageId)
  const hasCustomValue = !selected && !!(normalizeValue(chatId) || normalizeValue(replyToMessageId))
  const customId = `__telegram_legacy__:${telegramTargetKey(chatId, replyToMessageId)}`
  const value = selected?.id || (hasCustomValue ? customId : EMPTY_TELEGRAM_DESTINATION)

  const handleChange = (nextValue: string) => {
    if (nextValue === EMPTY_TELEGRAM_DESTINATION) {
      onChange({ chat_id: '', reply_to_message_id: null, destination: null })
      return
    }

    const nextDestination = options.find((option) => option.id === nextValue)
    if (nextDestination) {
      onChange({
        chat_id: nextDestination.chat_id,
        reply_to_message_id: nextDestination.reply_to_message_id || null,
        destination: nextDestination,
      })
    }
  }

  return (
    <Select
      value={value}
      onValueChange={handleChange}
      disabled={disabled || (options.length === 0 && !hasCustomValue && !allowEmpty)}
    >
      <SelectTrigger id={id} className={cn('w-full', triggerClassName, className)}>
        <SelectValue placeholder={options.length === 0 ? 'No Telegram destinations' : placeholder} />
      </SelectTrigger>
      <SelectContent>
        {allowEmpty && (
          <SelectItem value={EMPTY_TELEGRAM_DESTINATION}>{emptyLabel}</SelectItem>
        )}
        {hasCustomValue && (
          <SelectItem value={customId}>{customDestinationLabel(chatId, replyToMessageId)}</SelectItem>
        )}
        {options.map((destination) => (
          <SelectItem key={destination.id} value={destination.id}>
            {destination.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
